import { LAYOUTS, canUndo, faceGlyph, faceName, freeTiles, isStuck, isWon, layoutById, movesMade, tilesLeft } from '@strategy4kids/game-engine';
import { RoundSummaryPanel } from '../components/table/RoundSummaryPanel.js';
import { useT } from '../i18n/useT.js';
import { tileInk, tileMark } from '../lib/mahjong-marks.js';
import { useMahjongSolitaireStore } from '../state/mahjong-solitaire-store.js';

/**
 * Geometry. Positions are on the engine's half-tile grid (a tile is 2 wide, 2 tall in grid steps). A tile is 3:4, so one grid step down is
 * 4/3 of a grid step across. Higher layers are drawn a little up and to the left so the stack reads as a stack. Everything is a percentage
 * of the board, which has a minimum width (44 px per tile) so tap targets stay big and a phone scrolls sideways instead of shrinking tiles.
 */
const ROW = 4 / 3;
const LAYER_DX = 0.28;
const LAYER_DY = 0.34;
const TILE_W = 2;
const TILE_H = 2 * (4 / 3);
const MIN_TILE_PX = 44;
const MAX_TILE_PX = 120;

/** Mahjong Solitaire: pick two free tiles with the same face to remove them; clear the board to win. */
export function MahjongSolitaireScreen({ onExit }: { onExit: () => void }) {
  const { t } = useT('games');
  const game = useMahjongSolitaireStore((s) => s.game);
  const layoutId = useMahjongSolitaireStore((s) => s.layout);
  const seed = useMahjongSolitaireStore((s) => s.seed);
  const selected = useMahjongSolitaireStore((s) => s.selected);
  const hintPair = useMahjongSolitaireStore((s) => s.hintPair);
  const message = useMahjongSolitaireStore((s) => s.message);
  const assistFree = useMahjongSolitaireStore((s) => s.assistFree);
  const tap = useMahjongSolitaireStore((s) => s.tap);
  const hint = useMahjongSolitaireStore((s) => s.hint);
  const undo = useMahjongSolitaireStore((s) => s.undo);
  const reshuffle = useMahjongSolitaireStore((s) => s.reshuffle);
  const newGame = useMahjongSolitaireStore((s) => s.newGame);
  const setAssistFree = useMahjongSolitaireStore((s) => s.setAssistFree);

  if (!game) return null;
  const layout = layoutById(layoutId);
  const gone = new Set(game.removed);
  const free = new Set(freeTiles(game));
  const hinted = new Set(hintPair ?? []);
  const won = isWon(game);
  const stuck = isStuck(game);
  const maxX = Math.max(...layout.positions.map((p) => p.x));
  const maxY = Math.max(...layout.positions.map((p) => p.y));
  const maxLayer = Math.max(...layout.positions.map((p) => p.layer));
  const padX = maxLayer * LAYER_DX;
  const padY = maxLayer * LAYER_DY;
  const boardW = maxX + TILE_W + padX;
  const boardH = maxY * ROW + TILE_H + padY;
  const layoutName = (id: string) => t(`mj-layout-${id as 'flat' | 'pyramid' | 'turtle'}`);

  let status: string;
  if (won) status = t('mj-won');
  else if (stuck) status = message === 'dead' ? t('mj-dead') : t('mj-stuck');
  else if (message === 'blocked') status = t('mj-blocked');
  else if (message === 'no-match') status = t('mj-no-match');
  else if (selected !== null) status = t('mj-pick-second');
  else if (message === 'matched') status = t('mj-matched');
  else status = t('mj-pick-first');

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 lg:max-w-6xl lg:gap-6 lg:p-8 xl:max-w-7xl 2xl:max-w-[96rem]" data-testid="mahjong-solitaire-screen" data-appdom-scope="mahjong-solitaire">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold text-berry">{t('mj-title')}</h1>
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-fg-muted" data-testid="mj-seed">{t('mj-deal', { layout: layoutName(layoutId), seed })}</span>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={() => newGame()} data-testid="mj-new-game" data-appdom-id="mj-new-game" data-appdom-type="Button">{t('mj-new-game')}</button>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={onExit} data-testid="mj-exit" data-appdom-id="mj-exit" data-appdom-type="Button">{t('bridge-exit')}</button>
        </div>
      </header>

      <output className="min-h-[48px] glass glass-light flex flex-col gap-1 rounded-xl px-3 py-2 text-sm font-bold text-ink lg:text-base" data-testid="mj-status" data-appdom-id="mj-status" data-appdom-type="Text" aria-live="polite">
        <span>{status}</span>
      </output>

      <div className="glass glass-light flex flex-wrap items-center gap-2 rounded-2xl px-4 py-2.5 shadow-sm lg:gap-4" data-testid="mj-score-strip" data-appdom-id="mj-score-strip" data-appdom-type="Panel">
        <span className="font-display text-base font-bold text-fg lg:text-lg">{t('table-score')}</span>
        <span className="rounded-full border border-accent bg-accent-soft px-3 py-1 text-sm font-bold lg:text-base" data-testid="mj-tiles-left">{t('mj-tiles-left', { n: tilesLeft(game) })}</span>
        <span className="rounded-full border border-border bg-surface px-3 py-1 text-sm font-bold lg:text-base" data-testid="mj-moves">{t('mj-moves', { n: movesMade(game) })}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="mj-controls">
        <button type="button" onClick={hint} disabled={won} data-testid="mj-hint" className="min-h-[48px] rounded-xl border border-border px-4 py-2 text-base font-bold disabled:opacity-50">{t('mj-hint')}</button>
        <button type="button" onClick={undo} disabled={!canUndo(game)} data-testid="mj-undo" className="min-h-[48px] rounded-xl border border-border px-3 py-2 text-sm font-bold disabled:opacity-50">{t('mj-undo')}</button>
        <button
          type="button"
          onClick={reshuffle}
          disabled={won}
          data-testid="mj-reshuffle"
          className={`min-h-[48px] rounded-xl border px-3 py-2 text-sm font-bold disabled:opacity-50 ${stuck ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}
        >
          {t('mj-shuffle')}
        </button>
        <span className="ml-auto flex items-center gap-1" role="group" aria-label={t('mj-layout')}>
          {LAYOUTS.map((l) => (
            <button
              key={l.id}
              type="button"
              aria-pressed={layoutId === l.id}
              data-testid={`mj-layout-${l.id}`}
              onClick={() => newGame(l.id)}
              className={`rounded-lg border px-3 py-1 text-sm font-bold ${layoutId === l.id ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}
            >
              {layoutName(l.id)}
            </button>
          ))}
        </span>
      </div>

      <div className="overflow-x-auto rounded-2xl bg-emerald-800/90 p-3">
        {/* biome-ignore lint/a11y/useSemanticElements: a positioned stack of buttons, named for screen readers; a fieldset would break the layout */}
        <div
          role="group"
          aria-label={t('mj-board')}
          data-testid="mj-board"
          data-layout={layoutId}
          className="relative mx-auto w-full"
          style={{
            aspectRatio: `${boardW} / ${boardH}`,
            minWidth: `${(boardW / TILE_W) * MIN_TILE_PX}px`,
            maxWidth: `${(boardW / TILE_W) * MAX_TILE_PX}px`,
            containerType: 'inline-size',
          }}
        >
          {game.tiles.map((tile) => {
            if (gone.has(tile.id)) return null;
            const isFree = free.has(tile.id);
            const isSelected = selected === tile.id;
            const isHint = hinted.has(tile.id);
            const name = faceName(tile.face);
            const label = isSelected ? t('mj-tile-selected', { name }) : isFree ? t('mj-tile-free', { name }) : t('mj-tile-blocked', { name });
            const left = ((tile.x + padX - tile.layer * LAYER_DX) / boardW) * 100;
            const top = ((tile.y * ROW + padY - tile.layer * LAYER_DY) / boardH) * 100;
            return (
              <button
                key={tile.id}
                type="button"
                data-testid={`mj-tile-${tile.id}`}
                data-face={tile.face}
                data-free={isFree ? 'true' : 'false'}
                data-hint={isHint ? 'true' : undefined}
                data-selected={isSelected ? 'true' : undefined}
                aria-label={label}
                aria-pressed={isSelected}
                aria-disabled={!isFree}
                onClick={() => tap(tile.id)}
                className={`absolute flex select-none flex-col items-center justify-center rounded-md border bg-amber-50 shadow-[2px_3px_0_0_rgba(0,0,0,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tileInk(tile.face)} ${
                  isSelected ? 'border-accent ring-4 ring-accent' : isHint ? 'border-sun ring-4 ring-sun' : 'border-amber-200'
                } ${assistFree && !isFree ? 'brightness-75 saturate-50' : ''} ${assistFree && isFree ? 'brightness-110 ring-2 ring-white' : ''}`}
                style={{
                  left: `${left}%`,
                  top: `${top}%`,
                  width: `${(TILE_W / boardW) * 100}%`,
                  height: `${(TILE_H / boardH) * 100}%`,
                  zIndex: tile.layer * 100 + Math.round(tile.y),
                  fontSize: `${(TILE_W / boardW) * 100 * 0.95}cqw`,
                }}
              >
                <span aria-hidden="true" className="leading-none">{faceGlyph(tile.face)}</span>
                <span aria-hidden="true" className="font-display font-bold leading-none" style={{ fontSize: '0.3em' }}>{tileMark(tile.face)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {won && (
        <RoundSummaryPanel
          testid="mj-result"
          headlineTestid="mj-result-text"
          summary={{
            round: 1,
            outcome: 'win',
            headline: t('mj-won-stats', { moves: movesMade(game) }),
            detail: t('mj-won'),
            rows: [{ key: 'you', label: t('table-you'), delta: t('mj-moves', { n: movesMade(game) }), total: tilesLeft(game), you: true, leading: true }],
            gameOver: true,
          }}
          nextLabel={t('mj-play-again')}
          onNext={() => newGame()}
          nextTestid="mj-play-again"
        />
      )}

      {!won && stuck && (
        <div className="glass glass-light flex flex-wrap items-center justify-between gap-2 rounded-xl px-4 py-3" data-testid="mj-result" data-appdom-id="mj-result" data-appdom-type="Panel">
          <span className="text-lg font-extrabold text-ink lg:text-2xl" data-testid="mj-result-text">
            {message === 'dead' ? t('mj-dead') : t('mj-stuck')}
          </span>
          {message === 'dead' ? (
            <button type="button" className="min-h-[48px] rounded-xl bg-accent px-4 py-2 font-display font-semibold text-accent-fg disabled:opacity-50" onClick={undo} disabled={!canUndo(game)} data-testid="mj-dead-undo">{t('mj-undo')}</button>
          ) : (
            <button type="button" className="min-h-[48px] rounded-xl bg-accent px-4 py-2 font-display font-semibold text-accent-fg" onClick={reshuffle} data-testid="mj-stuck-shuffle">{t('mj-shuffle')}</button>
          )}
        </div>
      )}

      <details className="rounded-2xl border border-border bg-surface p-3" data-testid="mj-settings">
        <summary className="flex min-h-[48px] cursor-pointer items-center text-sm font-extrabold uppercase tracking-wide text-fg-muted">{t('mj-assists')}</summary>
        <div className="mt-3 flex flex-col gap-3 text-sm font-bold">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={assistFree} onChange={(e) => setAssistFree(e.target.checked)} data-testid="mj-assist-free" />
            {t('mj-assist-free')}
          </label>
          <p className="font-normal text-fg-muted">{t('mj-teaching-note')}</p>
        </div>
      </details>
    </div>
  );
}
